// react
import { ChangeEvent, FormEvent, useContext, useMemo, useState } from 'react';

// types
import type { Contract, Entity, Project } from '../../../@types';

// context
import { ContextUser } from '../../../App.context';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Alert, Button, Grid, SelectChangeEvent } from '@mui/material';

// fields
import { FilePicker } from '../../fields/FilePicker/FilePicker';
import { Input } from '../../fields/Input/Input';
import { SelectCurrency } from '../../fields/SelectCurrency/SelectCurrency';

// services
import { postInvoice } from '../../../services/invoices';
import { postDocuments } from '../../../services/documents';

export interface FormAddInvoiceProps {
	contract: Partial<Contract>;
	onCancel: () => void;
	onSuccess: (e: FormEvent<HTMLFormElement>) => void;
}

export interface FormAddInvoiceValues {
	invoiceNumber: string;
	description: string;
	date: string;
	dateDue: string;
	currency: string;
	amount: string;
	tax: string;
}

export const FormAddInvoice = ({ contract, onCancel, onSuccess }: FormAddInvoiceProps) => {
	// hooks
	const { t } = useTranslation();

	// memos
	const currencies = useMemo(
		() => Array.from(new Set(contract.amounts?.map((amount) => amount.currency) || [])),
		[contract.amounts]
	);

	// context
	const { user } = useContext(ContextUser);

	// state
	const [values, setValues] = useState<FormAddInvoiceValues>({
		invoiceNumber: '',
		description: '',
		date: '',
		dateDue: '',
		currency: '',
		amount: '',
		tax: '',
	});
	const [files, setFiles] = useState<File[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);

	// vars
	const formId = 'form-add-invoice';
	const contractProject = contract?.project as Project;
	const contractAmount = contract?.amounts?.find(
		(amount) => amount.currency === values.currency
	)?.amount;
	const isAmountGreaterThanContract = contractAmount && Number(values.amount) > contractAmount;
	const userEntities = user?.entities as Entity[];
	const userSupplier = userEntities?.find((entity) => entity.type === 'SUPPLIER');

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: name === 'amount' || name === 'tax' ? Number(value) : value,
		});
	};

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setError(null);
		setIsSubmitting(true);

		try {
			const invoiceId = await postInvoice({
				amount: Number(values.amount),
				contract: Number(contract.id),
				currency: values.currency.trim().toUpperCase(),
				date: values.date,
				num: values.invoiceNumber.trim(),
				dateDue: values.dateDue,
				description: values.description.trim(),
				status: 'PENDING_REVIEW',
				supplier: userSupplier?.id || null,
				tax: Number(values.tax),
			});

			if (files.length > 0) {
				await Promise.all(
					files.map((file) =>
						postDocuments({
							file,
							contractId: Number(contract.id),
							invoiceId,
							projectId: contractProject?.id,
						})
					)
				);
			}

			onSuccess(e);
		} catch (err) {
			setError(err instanceof Error ? err.message : t('error.default'));
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<form id={formId} onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Input
						id={`${formId}-number`}
						label={t('number')}
						name="invoiceNumber"
						onChange={handleChangeValue}
						required={true}
						size="small"
						value={values.invoiceNumber}
					/>
				</Grid>
				<Grid item xs={12}>
					<Input
						id={`${formId}-description`}
						label={t('description')}
						name="description"
						onChange={handleChangeValue}
						required={true}
						rows={4}
						multiline
						size="small"
						value={values.description}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Input
						id={`${formId}-date`}
						label={t('date')}
						name="date"
						onChange={handleChangeValue}
						required={true}
						size="small"
						type="date"
						value={values.date}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Input
						id={`${formId}-date-due`}
						label={t('dateDue')}
						name="dateDue"
						onChange={handleChangeValue}
						required={true}
						size="small"
						type="date"
						value={values.dateDue}
					/>
				</Grid>
				<Grid item xs={12} md={4}>
					<SelectCurrency
						currencies={currencies}
						id={`${formId}-currency`}
						label={t('currency')}
						name="currency"
						onChange={handleChangeValue}
						required={true}
						value={values.currency}
					/>
				</Grid>
				<Grid item xs={12} md={4}>
					<Input
						id={`${formId}-amount`}
						label={t('amount')}
						name="amount"
						inputProps={{ min: 0, step: '0.01' }}
						onChange={handleChangeValue}
						required={true}
						size="small"
						type="number"
						value={values.amount}
					/>
				</Grid>
				<Grid item xs={12} md={4}>
					<Input
						id={`${formId}-tax`}
						inputProps={{ min: 0, step: '0.01' }}
						label={t('tax')}
						name="tax"
						onChange={handleChangeValue}
						size="small"
						type="number"
						value={values.tax}
					/>
				</Grid>
				{isAmountGreaterThanContract && (
					<Grid item xs={12}>
						<Alert severity="warning">{t('alert.invoiceGreaterThanContract')}</Alert>
					</Grid>
				)}
				<Grid item xs={12}>
					<FilePicker
						onChange={(e, filesSelected) => {
							setFiles(filesSelected);
						}}
					/>
				</Grid>
				{error && (
					<Grid item xs={12}>
						<Alert severity="error" variant="outlined">
							{error}
						</Alert>
					</Grid>
				)}
				<Grid container item justifyContent="flex-end" spacing={1} xs={12}>
					<Grid item>
						<Button disabled={isSubmitting} onClick={onCancel} variant="outlined">
							{t('cancel')}
						</Button>
					</Grid>
					<Grid item>
						<Button disabled={isSubmitting} type="submit" variant="contained">
							{isSubmitting ? t('loading') : t('submitInvoice')}
						</Button>
					</Grid>
				</Grid>
			</Grid>
		</form>
	);
};
